import { useState, useEffect } from 'react'
import { MessageCircle, Target, Shield, Zap, BadgeCheck, CheckCircle2 } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal.js'
import InputField from '../ui/InputField.jsx'
import SelectField from '../ui/SelectField.jsx'
import Button from '../ui/Button.jsx'
import { SERVICE_CATEGORIES } from '../../constants/categories.js'
import { registerMerchant } from '../../services/merchantApi.js'

/* ─────────────────────────────────────────────────────────────
   Constants
   ───────────────────────────────────────────────────────────── */

const EMPTY_FORM = {
  consent: false,
  businessName: '',
  merchantName: '',
  whatsappNumber: '',
  serviceCategory: '',
  area: '',
  operatingDays: '2',       // default: Mon-Sat
  openingTime: '',
  closingTime: '',
  minPrice: '',
  maxDiscountPct: '',
}

const OPERATING_DAYS_OPTIONS = [
  { value: '1', label: 'Monday – Friday' },
  { value: '2', label: 'Monday – Saturday' },
  { value: '3', label: 'Every day' },
]

const DAYS_MAP = {
  '1': ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  '2': ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
  '3': ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
}

const STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
})

/* ─────────────────────────────────────────────────────────────
   Input sanitisation helpers
   ───────────────────────────────────────────────────────────── */

function sanitiseText(str, { trim = false } = {}) {
  const cleaned = str
    .replace(/<[^>]*>/g, '')
    .replace(/\0/g, '')
  return trim ? cleaned.trim() : cleaned
}

function sanitisePhone(str) {
  return str.replace(/[^\d+]/g, '')
}

function sanitiseField(name, value, { trim = false } = {}) {
  if (name === 'whatsappNumber') return sanitisePhone(value)
  if (name === 'minPrice' || name === 'maxDiscountPct') return value
  if (name === 'consent' || name === 'operatingDays') return value
  return sanitiseText(value, { trim })
}

/* ─────────────────────────────────────────────────────────────
   Validation
   ───────────────────────────────────────────────────────────── */

const FIELD_MAX_LENGTHS = {
  businessName: 100,
  merchantName: 100,
  whatsappNumber: 20,
}

const PHONE_RE = /^\+?[1-9]\d{6,14}$/

// Match time formats: 9, 09, 9:00, 09:00, 9AM, 9:00AM, 9 AM, 9:00 AM
const TIME_RE = /^(\d{1,2})(:\d{2})?\s*(AM|PM)?$/i

function validate(values) {
  const errors = {}

  if (!values.consent)
    errors.consent = 'You must agree to receive mission requests.'

  if (!values.businessName)
    errors.businessName = 'Business name is required.'
  else if (values.businessName.length < 2)
    errors.businessName = 'At least 2 characters.'
  else if (values.businessName.length > FIELD_MAX_LENGTHS.businessName)
    errors.businessName = `Max ${FIELD_MAX_LENGTHS.businessName} characters.`

  if (!values.merchantName)
    errors.merchantName = 'Your name is required.'
  else if (values.merchantName.length < 2)
    errors.merchantName = 'At least 2 characters.'
  else if (values.merchantName.length > FIELD_MAX_LENGTHS.merchantName)
    errors.merchantName = `Max ${FIELD_MAX_LENGTHS.merchantName} characters.`

  if (!values.whatsappNumber)
    errors.whatsappNumber = 'Phone number is required.'
  else if (!PHONE_RE.test(values.whatsappNumber))
    errors.whatsappNumber = 'Enter a valid number, e.g. +233201234567.'

  if (!values.serviceCategory)
    errors.serviceCategory = 'Please select a service category.'

  if (!values.area)
    errors.area = 'Please select your area.'

  if (!values.openingTime)
    errors.openingTime = 'Opening time is required.'
  else if (!TIME_RE.test(values.openingTime.trim()))
    errors.openingTime = 'Enter a valid time, e.g. 9:00 AM, 9, 08:30'

  if (!values.closingTime)
    errors.closingTime = 'Closing time is required.'
  else if (!TIME_RE.test(values.closingTime.trim()))
    errors.closingTime = 'Enter a valid time, e.g. 7:00 PM, 19, 18:30'

  if (!values.minPrice || values.minPrice === '') {
    errors.minPrice = 'Minimum price is required.'
  } else {
    const price = Number(values.minPrice)
    if (isNaN(price) || price <= 0)
      errors.minPrice = 'Minimum price must be greater than 0.'
  }

  if (values.maxDiscountPct === '' || values.maxDiscountPct === null || values.maxDiscountPct === undefined) {
    errors.maxDiscountPct = 'Maximum discount is required.'
  } else {
    const pct = Number(values.maxDiscountPct)
    if (isNaN(pct) || pct < 0 || pct > 50)
      errors.maxDiscountPct = 'Discount must be between 0 and 50%.'
  }

  return errors
}

/* ─────────────────────────────────────────────────────────────
   Time parsing (same logic as backend _parse_time)
   ───────────────────────────────────────────────────────────── */

function parseTimeToHHMM(input) {
  if (!input) return null
  const text = input.trim().toUpperCase().replace('.', ':')

  const patterns = [
    { re: /^(\d{1,2}):(\d{2})\s*(AM|PM)$/, fmt: 'hm_ampm' },
    { re: /^(\d{1,2})\s*(AM|PM)$/, fmt: 'h_ampm' },
    { re: /^(\d{1,2}):(\d{2})$/, fmt: 'hm_24' },
    { re: /^(\d{1,2})$/, fmt: 'h_only' },
  ]

  for (const { re, fmt } of patterns) {
    const m = text.match(re)
    if (!m) continue
    let h, mi
    if (fmt === 'hm_ampm') {
      h = parseInt(m[1]); mi = parseInt(m[2]); const ap = m[3]
      if (ap === 'PM' && h !== 12) h += 12
      if (ap === 'AM' && h === 12) h = 0
    } else if (fmt === 'h_ampm') {
      h = parseInt(m[1]); mi = 0; const ap = m[2]
      if (ap === 'PM' && h !== 12) h += 12
      if (ap === 'AM' && h === 12) h = 0
    } else if (fmt === 'hm_24') {
      h = parseInt(m[1]); mi = parseInt(m[2])
    } else {
      h = parseInt(m[1]); mi = 0
    }
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) {
      return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
    }
  }
  return null
}

/* ─────────────────────────────────────────────────────────────
   Trust points
   ───────────────────────────────────────────────────────────── */
const TRUST_POINTS = [
  { icon: MessageCircle, text: 'Works entirely in the DecidA app. No extra tools needed.' },
  { icon: Target, text: 'Only get requests that match what you do and where you work.' },
  { icon: Shield, text: 'We limit how many requests you get. No pile-ups.' },
  { icon: Zap, text: 'Respond in one tap: YES \u00b7 LATER \u00b7 DECLINE' },
  { icon: BadgeCheck, text: '100% free. Always.' },
]

/* ─────────────────────────────────────────────────────────────
   Component
   ───────────────────────────────────────────────────────────── */
export default function MerchantForm() {
  const leftRef = useScrollReveal()
  const rightRef = useScrollReveal({ threshold: 0.1 })
  const [values, setValues] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [status, setStatus] = useState(STATUS.IDLE)
  const [apiError, setApiError] = useState('')
  const [areaOptions, setAreaOptions] = useState([])

  // Fetch areas from backend (same list as mobile app)
  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
    fetch(`${API_BASE}/api/areas/`)
      .then((r) => r.ok ? r.json() : [])
      .then((areas) => {
        setAreaOptions([
          { value: '', label: 'Select your area', disabled: true },
          ...areas.map((a) => ({ value: a, label: a })),
        ])
      })
      .catch(() => setAreaOptions([]))
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newVal = type === 'checkbox' ? checked : sanitiseField(name, value)
    setValues((prev) => ({ ...prev, [name]: newVal }))
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validate({ ...values, [name]: newVal })[name],
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target
    const trimmed = type === 'checkbox' ? checked : sanitiseField(name, value, { trim: true })
    setValues((prev) => ({ ...prev, [name]: trimmed }))
    setTouched((prev) => ({ ...prev, [name]: true }))
    setErrors((prev) => ({ ...prev, [name]: validate({ ...values, [name]: trimmed })[name] }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const allTouched = Object.fromEntries(
      Object.keys(EMPTY_FORM).map((k) => [k, true])
    )
    setTouched(allTouched)

    const validationErrors = validate(values)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setStatus(STATUS.LOADING)
    setApiError('')

    const result = await registerMerchant({
      ...values,
      operatingDays: DAYS_MAP[values.operatingDays] || DAYS_MAP['2'],
      openingTime: parseTimeToHHMM(values.openingTime),
      closingTime: parseTimeToHHMM(values.closingTime),
      minPrice: Number(values.minPrice),
      maxDiscountPct: Number(values.maxDiscountPct),
    })

    if (result.ok) {
      setStatus(STATUS.SUCCESS)
      setValues(EMPTY_FORM)
      setTouched({})
      setErrors({})
    } else {
      setStatus(STATUS.ERROR)
      if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...result.fieldErrors }))
        setTouched((prev) => ({
          ...prev,
          ...Object.fromEntries(Object.keys(result.fieldErrors).map((k) => [k, true])),
        }))
      }
      setApiError(result.generalError ?? 'Something went wrong. Please try again.')
    }
  }

  if (status === STATUS.SUCCESS) {
    return (
      <section id="join" className="py-24 bg-white">
        <div className="section-container">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-primary-light flex items-center
                            justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-primary" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-extrabold text-brand-dark mb-3">
              You&rsquo;re in!
            </h2>
            <p className="text-base text-brand-gray leading-relaxed">
              Welcome to DecidA. Our team will be in touch with next steps.
            </p>
            <button
              onClick={() => setStatus(STATUS.IDLE)}
              className="mt-8 text-sm font-semibold text-primary hover:underline"
            >
              Register another business
            </button>
          </div>
        </div>
      </section>
    )
  }

  const isLoading = status === STATUS.LOADING

  return (
    <section id="join" className="py-24 bg-white">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* ── Left: pitch copy ─────────────────────── */}
          <div ref={leftRef} className="reveal-left lg:sticky lg:top-24">
            <p className="eyebrow mb-4">
              <span className="w-5 h-px bg-primary" />
              Accra &middot; Early Access
            </p>
            <h2
              className="font-extrabold text-brand-dark mb-5"
              style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)' }}
            >
              Join DecidA<br />as a Merchant
            </h2>
            <p className="text-base text-brand-gray leading-relaxed mb-8 max-w-sm">
              We&apos;re onboarding the first wave of merchants in Accra.
              Submit your details and we'll be in touch with next
              steps instantly. No manual process.
            </p>

            <ul className="flex flex-col gap-4" role="list">
              {TRUST_POINTS.map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 text-sm font-medium text-brand-dark"
                >
                  <div className="w-7 h-7 rounded-lg bg-primary-light flex items-center
                                  justify-center flex-shrink-0 mt-px">
                    <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                  </div>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          {/* ── Right: form card ─────────────────────── */}
          <div ref={rightRef} className="reveal-right card-elevated p-8 md:p-10">
            <h3 className="text-lg font-bold text-brand-dark mb-7">
              Merchant Registration
            </h3>

            <form
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
              className="flex flex-col gap-5"
            >
              {/* Consent checkbox */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={values.consent}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary
                               focus:ring-primary"
                  />
                  <span className="text-sm text-brand-dark leading-relaxed">
                    I agree to receive mission requests from buyers via the DecidA app.
                    <span className="text-red-500 ml-0.5">*</span>
                  </span>
                </label>
                {touched.consent && errors.consent && (
                  <p className="text-xs text-red-500 font-medium mt-1 ml-7">{errors.consent}</p>
                )}
              </div>

              <InputField
                label="Business Name"
                id="businessName"
                name="businessName"
                type="text"
                placeholder="e.g. QuickFix Phone Repairs"
                value={values.businessName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.businessName ? errors.businessName : undefined}
                maxLength={FIELD_MAX_LENGTHS.businessName}
                required
                disabled={isLoading}
                autoComplete="organization"
              />

              <InputField
                label="Your Name"
                id="merchantName"
                name="merchantName"
                type="text"
                placeholder="e.g. Kwame Mensah"
                value={values.merchantName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.merchantName ? errors.merchantName : undefined}
                maxLength={FIELD_MAX_LENGTHS.merchantName}
                required
                disabled={isLoading}
                autoComplete="name"
              />

              <div>
                <label htmlFor="whatsappNumber"
                  className="text-sm font-semibold text-brand-dark block mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex items-stretch">
                  <span className="inline-flex items-center px-3 rounded-l-xl border border-r-0
                                   border-gray-200 bg-gray-50 text-sm font-semibold text-brand-dark
                                   select-none flex-shrink-0">
                    +
                  </span>
                  <input
                    id="whatsappNumber"
                    name="whatsappNumber"
                    type="tel"
                    inputMode="numeric"
                    placeholder="233201234567"
                    value={values.whatsappNumber.replace(/^\+/, '')}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '')
                      handleChange({ target: { name: 'whatsappNumber', value: '+' + digits } })
                    }}
                    onBlur={handleBlur}
                    maxLength={15}
                    required
                    disabled={isLoading}
                    autoComplete="tel"
                    className={`form-field rounded-l-none flex-1 ${touched.whatsappNumber && errors.whatsappNumber ? 'form-field-error' : ''
                      }`}
                  />
                </div>
                {touched.whatsappNumber && errors.whatsappNumber && (
                  <p className="text-xs text-red-500 font-medium mt-0.5">{errors.whatsappNumber}</p>
                )}
                <p className="mt-1.5 text-xs text-brand-gray">
                  Include your country code. Buyer requests will arrive on this number.
                </p>
              </div>

              <SelectField
                label="Service Category"
                id="serviceCategory"
                name="serviceCategory"
                options={SERVICE_CATEGORIES}
                value={values.serviceCategory}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.serviceCategory ? errors.serviceCategory : undefined}
                required
                disabled={isLoading}
              />

              <SelectField
                label="Area / Neighbourhood"
                id="area"
                name="area"
                options={areaOptions}
                value={values.area}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.area ? errors.area : undefined}
                required
                disabled={isLoading}
              />

              <SelectField
                label="Operating Days"
                id="operatingDays"
                name="operatingDays"
                options={OPERATING_DAYS_OPTIONS}
                value={values.operatingDays}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.operatingDays ? errors.operatingDays : undefined}
                required
                disabled={isLoading}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Opening Time"
                  id="openingTime"
                  name="openingTime"
                  type="text"
                  placeholder="e.g. 9:00 AM, 9, 08:30"
                  value={values.openingTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.openingTime ? errors.openingTime : undefined}
                  required
                  disabled={isLoading}
                />
                <InputField
                  label="Closing Time"
                  id="closingTime"
                  name="closingTime"
                  type="text"
                  placeholder="e.g. 7:00 PM, 19, 18:30"
                  value={values.closingTime}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.closingTime ? errors.closingTime : undefined}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <InputField
                    label="Minimum Price (GHS)"
                    id="minPrice"
                    name="minPrice"
                    type="number"
                    placeholder="e.g. 50"
                    value={values.minPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.minPrice ? errors.minPrice : undefined}
                    required
                    disabled={isLoading}
                    min="0.01"
                    step="0.01"
                  />
                  <p className="mt-1.5 text-xs text-brand-gray">
                    The lowest price you accept for any job.
                  </p>
                </div>
                <div>
                  <InputField
                    label="Max Discount (%)"
                    id="maxDiscountPct"
                    name="maxDiscountPct"
                    type="number"
                    placeholder="e.g. 10"
                    value={values.maxDiscountPct}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxDiscountPct ? errors.maxDiscountPct : undefined}
                    required
                    disabled={isLoading}
                    min="0"
                    max="50"
                    step="1"
                  />
                  <p className="mt-1.5 text-xs text-brand-gray">
                    Maximum discount you'd offer (0-50%).
                  </p>
                </div>
              </div>

              {status === STATUS.ERROR && apiError && (
                <div
                  role="alert"
                  className="rounded-xl bg-red-50 border border-red-200 px-4 py-3"
                >
                  <p className="text-sm text-red-600 font-medium">{apiError}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="btn-shimmer w-full !py-4 !text-[15px] mt-1
                           hover:scale-[1.02] active:scale-[0.98]"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting&hellip;
                  </>
                ) : (
                  'Join Now'
                )}
              </Button>

            </form>
          </div>

        </div>
      </div>
    </section>
  )
}
